import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useRobots } from "../../../hooks/robot";
import { useLocationPoints } from "../../../hooks/location";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { DEFAULT_DATEFORMAT } from "../../scheduledJob/_function/common";
import { getMaxEndDate, getMaxStartDate } from "../../../lib/api/function";
import { useSnackbar } from "notistack";
import { COLOR } from "../../../utils/colors";

export type FilterResponseType = {
  robot?: string;
  status?: string;
  startLocation?: string;
  endLocation?: string;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
};

export default function JobFilterDialog({
  open,
  onChangeOpen,
  selectedRobotName,
  selectedStatus,
  selectedStartLocation,
  selectedEndLocation,
  selectedStartDate,
  selectedEndDate,
  error,
  endDateMonthLimit = 6,
  onChangeFilter,
  hideFilters = []
}: {
  open?: boolean;
  onChangeOpen?: (value: boolean) => void;
  selectedRobotName?: string;
  selectedStatus?: string;
  selectedStartLocation?: string;
  selectedEndLocation?: string;
  selectedStartDate: Dayjs | null;
  selectedEndDate: Dayjs | null;
  error?: string;
  endDateMonthLimit?: number;
  hideFilters?: string[];
  onChangeFilter?: ({
    robot,
    status,
    startLocation,
    endLocation,
    startDate,
    endDate,
  }: FilterResponseType) => void;
}) {
  const [isOpen, setOpen] = useState(open || false);
  const [robot, setRobot] = useState(selectedRobotName || "all");
  const [status, setStatus] = useState(selectedStatus || "all");
  const [startLocation, setStartLocation] = useState(selectedStartLocation || "all");
  const [endLocation, setEndLocation] = useState(selectedEndLocation || "all");
  const { enqueueSnackbar } = useSnackbar();

  const [startDate, setStartDate] = useState<Dayjs | null>(selectedStartDate || null);
  const [endDate, setEndDate] = useState<Dayjs | null>(selectedEndDate || null);

  useEffect(() => {
    setStatus(selectedStatus || "all");
    setRobot(selectedRobotName || "all");
    setStartDate(selectedStartDate || null);
    setStartLocation(selectedStartLocation || "all");
    setEndDate(selectedEndDate || null);
    setEndLocation(selectedEndLocation || "all");
  }, [isOpen, selectedStatus, selectedRobotName, selectedStartDate, selectedStartLocation, selectedEndDate, selectedEndLocation]);

  useEffect(() => {
    setRobot(selectedRobotName || "all");
  }, [selectedRobotName]);

  useEffect(() => {
    setStatus(selectedStatus || "all");
  }, [selectedStatus]);

  useEffect(() => {
    setStartLocation(selectedStartLocation || "all");
  }, [selectedStartLocation]);

  useEffect(() => {
    setEndLocation(selectedEndLocation || "all");
  }, [selectedEndLocation]);

  useEffect(() => {
    setStartDate(selectedStartDate || null);
  }, [selectedStartDate]);

  useEffect(() => {
    setEndDate(selectedEndDate || null);
  }, [selectedEndDate]);

  const { data: dataRobot } = useRobots({ enabled: open });
  const robots = dataRobot?.data || [];

  const { data: dataLocation } = useLocationPoints("waypoint", { pageSize: 100 });
  const allLocations = dataLocation?.data || [];

  const handleChangeOpen = (value: boolean) => {
    setOpen(value);
    onChangeOpen?.(value);
  };

  useEffect(() => {
    setOpen(!!open);
  }, [open]);

  const handleStartDateChange = (newStartDate: Dayjs | null) => {
    const today = dayjs();
    if (!newStartDate || !dayjs.isDayjs(newStartDate) || newStartDate.isAfter(today)) {
      setStartDate(null);
      return;
    }

    const startOfDay = dayjs(newStartDate).startOf("day");
    setStartDate(startOfDay);

    if (endDate && startOfDay.isBefore(endDate.subtract(endDateMonthLimit, "month"))) {
      const maxEndDate = getMaxEndDate(startOfDay, endDateMonthLimit, "months");
      enqueueSnackbar(
        "End Date changed to " +
          maxEndDate.format(DEFAULT_DATEFORMAT) +
          " due to maximum limit daterange is " +
          endDateMonthLimit +
          " months",
        {
          variant: "warning",
          persist: false,
          style: {
            backgroundColor: COLOR.neutral[100],
            color: COLOR.black,
            fontFamily: "Poppins"
          },
        }
      );
      setEndDate(maxEndDate);
    }
  };

  const handleEndDateChange = (newEndDate: Dayjs | null) => {
    const today = dayjs();
    if (!newEndDate || !dayjs.isDayjs(newEndDate) || newEndDate.isAfter(today)) {
      setEndDate(null);
      return;
    }
    const endOfDay = dayjs(newEndDate).endOf("day");
    setEndDate(endOfDay);
  };

  const resetFilters = () => {
    setRobot("all");
    setStatus("all");
    setStartDate(null);
    setEndDate(null);
    setStartLocation("all");
    setEndLocation("all");
  };

  const handleResetFilters = () => {
    resetFilters();
    onChangeFilter?.({
      robot: "all",
      endDate: null,
      startDate: null,
      startLocation: "all",
      endLocation: "all",
      status: "all"
    });
    handleChangeOpen(false);
  };

  const handleApplyFilters = () => {
    const payload: FilterResponseType = {
      robot,
      endDate,
      startDate,
      startLocation,
      endLocation,
      status
    };

    if (payload.endDate && !payload.startDate) {
      enqueueSnackbar("Please select a start date before selecting an end date.", {
        variant: "warning",
        persist: false,
        style: {
          backgroundColor: COLOR.accent.warning,
          color: COLOR.white,
          fontFamily: "Poppins"
        },
      });
      return;
    }

    if (payload.startDate && !payload.endDate) {
      payload.endDate = dayjs();
      setEndDate(dayjs());
    }

    onChangeFilter?.(payload);
    handleChangeOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={() => handleChangeOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        Filter By
        <IconButton
          aria-label="close"
          onClick={() => handleChangeOpen(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#a5a5a5"
          }}
        >
          <GridCloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
          <InputLabel id="robot-id-label">Robot Name</InputLabel>
          <Select
            labelId="robot-id-label"
            id="robot_id"
            name="robot_id"
            label="Robot Name"
            value={robot}
            onChange={(e) => setRobot(e.target.value)}
          >
            <MenuItem value="all">All Robots</MenuItem>
            {robots.map((robot, index) => (
              <MenuItem key={index} value={robot.robot_name}>
                {robot.robot_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            id="status"
            name="status"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  overflowY: "auto",
                },
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        {!hideFilters.includes("startLocation") && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="start-location-label">Start Location</InputLabel>
            <Select
              labelId="start-location-label"
              id="startLocation"
              name="startLocation"
              label="Start Location"
              value={startLocation}
              onChange={(e) => setStartLocation(e.target.value)}
            >
              <MenuItem value="all">All Start Locations</MenuItem>
              {allLocations.map((location, index) => (
                <MenuItem key={index} value={location.location_name}>
                  {location.location_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!hideFilters.includes("endLocation") && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="end-location-label">End Location</InputLabel>
            <Select
              labelId="end-location-label"
              id="endLocation"
              name="endLocation"
              label="End Location"
              value={endLocation}
              onChange={(e) => setEndLocation(e.target.value)}
            >
              <MenuItem value="all">All End Locations</MenuItem>
              {allLocations.map((location, index) => (
                <MenuItem key={index} value={location.location_name}>
                  {location.location_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography sx={{ mb: 2 }}>Select Date Range:</Typography>

        <Grid container spacing={2}>
          <Grid item xs={5.5}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onAccept={handleStartDateChange}
              maxDate={getMaxStartDate(endDate)}
              format={DEFAULT_DATEFORMAT}
            />
          </Grid>

          <Grid item xs={0.5}>
            <Typography sx={{ mt: 2 }}>-</Typography>
          </Grid>

          <Grid item xs={5.5}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              minDate={startDate ?? undefined}
              maxDate={getMaxEndDate(startDate, endDateMonthLimit, "months")}
              format={DEFAULT_DATEFORMAT}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleResetFilters} sx={{ display: "block", width: "100%" }}>
          Reset Filters
        </Button>
        <Button onClick={handleApplyFilters} sx={{ display: "block", width: "100%" }}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
}